import { useState } from 'react'
import {
  TextInput,
  NumberInput,
  SelectInput,
  FormDataConsumer,
  useNotify,
} from 'react-admin'
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import { useWatch, useFormContext } from 'react-hook-form'

import CustomFileInput from '../../components/CustomFileInput'
import CustomFileField from '../../components/CustomFileField'
import CustomAudioPlayerField from '../../components/CustomAudioPlayerField'
import { validateRequired } from '../../utils/validate'
import feathersClient from '../../feathersClient'
import { extractUrlFromUploadData } from '../../utils/fileUploadHelpers'

const ChaptersManagerInput = ({ voiceActors = [] }) => {
  const { setValue, getValues } = useFormContext()
  const chapters = useWatch({ name: 'chapters' }) || []
  const multiVoices = useWatch({ name: 'multiVoices' })
  const bookId = useWatch({ name: 'id' })
  const notify = useNotify()

  const [selectedChapterIndex, setSelectedChapterIndex] = useState(0)
  const [saving, setSaving] = useState(false)
  const [removingIndex, setRemovingIndex] = useState(null)

  const selectedChapter = chapters[selectedChapterIndex]

  const addChapter = () => {
    const current = getValues('chapters') || []
    const newChapter = {
      order: current.length + 1,
      name: '',
      url: '',
      duration: null,
      voices: voiceActors.map(va => ({
        voiceId: va.id,
        url: null,
        duration: null,
      })),
      attachments: [],
    }
    setValue('chapters', [...current, newChapter])
    setSelectedChapterIndex(current.length)
  }

  const removeChapter = async index => {
    const current = getValues('chapters') || []
    const chapter = current[index]

    if (chapter?.id) {
      try {
        setRemovingIndex(index)
        await feathersClient
          .get()
          .service('chapters')
          .remove(chapter.id, { query: { bookId } })
      } catch (err) {
        notify(typeof err === 'string' ? err : 'Failed to delete chapter', {
          type: 'error',
        })
        return
      } finally {
        setRemovingIndex(null)
      }
    }

    const updated = current.filter((_, i) => i !== index)
    setValue('chapters', updated, { shouldDirty: true })
    setSelectedChapterIndex(prev =>
      prev >= updated.length ? Math.max(0, updated.length - 1) : prev,
    )
  }

  const removeVoice = vIdx => {
    const current = getValues('chapters') || []
    const chapter = current[selectedChapterIndex]
    if (!chapter) return
    const newVoices = (chapter.voices || []).filter((_, i) => i !== vIdx)
    const updated = [...current]
    updated[selectedChapterIndex] = { ...chapter, voices: newVoices }
    setValue('chapters', updated)
  }

  const addAttachment = () => {
    const current = getValues('chapters') || []
    const chapter = current[selectedChapterIndex]
    if (!chapter) return
    const newAttachments = [
      ...(chapter.attachments || []),
      { name: '', url: null },
    ]
    const updated = [...current]
    updated[selectedChapterIndex] = { ...chapter, attachments: newAttachments }
    setValue('chapters', updated)
  }

  const removeAttachment = aIdx => {
    const current = getValues('chapters') || []
    const chapter = current[selectedChapterIndex]
    if (!chapter) return
    const newAttachments = (chapter.attachments || []).filter(
      (_, i) => i !== aIdx,
    )
    const updated = [...current]
    updated[selectedChapterIndex] = { ...chapter, attachments: newAttachments }
    setValue('chapters', updated)
  }

  const saveChapter = async () => {
    const current = getValues('chapters') || []
    const chapter = current[selectedChapterIndex]
    if (!chapter) return

    try {
      setSaving(true)

      const voices = []
      for (const voice of chapter.voices || []) {
        const { url, duration } = extractUrlFromUploadData(
          voice.url,
          `Voice ${voice.voiceId}`,
        )
        voices.push({
          voiceId: voice.voiceId,
          url: url || '',
          duration: duration || voice.duration || 0,
        })
      }

      const attachments = []
      for (const att of chapter.attachments || []) {
        const { url } = extractUrlFromUploadData(
          att.url,
          `Attachment ${att.name}`,
        )
        attachments.push({
          ...(att.id ? { id: att.id } : {}),
          name: att.name,
          url: url || '',
        })
      }

      const payload = {
        bookId,
        name: chapter.name,
        url: chapter.url || '',
        duration: chapter.duration || 0,
        order: chapter.order,
        voices,
        attachments,
      }

      if (chapter.id) {
        await feathersClient
          .get()
          .service('chapters')
          .patch(chapter.id, payload)
      } else {
        const created = await feathersClient
          .get()
          .service('chapters')
          .create(payload)
        const updated = [...current]
        updated[selectedChapterIndex] = { ...chapter, id: created.id }
        setValue('chapters', updated, { shouldDirty: true })
      }

      notify('Chapter saved successfully', { type: 'success' })
    } catch (err) {
      notify(typeof err === 'string' ? err : 'Failed to save chapter', {
        type: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  const chapterKey = `chapter-${selectedChapterIndex}`
  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        minWidth: 0,
        height: '680px',
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        overflow: 'hidden',
      }}>
      {/* Left Panel - Chapter list */}
      <Box
        sx={{
          width: '15%',
          borderRight: '1px solid #e0e0e0',
          display: 'flex',
          flexDirection: 'column',
        }}>
        <Box
          sx={{
            p: 1.5,
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Chapters
          </Typography>
          <IconButton
            size="small"
            onClick={addChapter}
            color="primary"
            title="Add chapter">
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}>
          {chapters.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
              No chapters yet
            </Typography>
          )}
          {chapters.map((chapter, idx) => {
            const isActive = selectedChapterIndex === idx
            return (
              <Box
                key={idx}
                onClick={() => setSelectedChapterIndex(idx)}
                sx={{
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 2,
                  border: '1.5px solid',
                  borderColor: isActive ? '#c5c1c1ff' : '#e8e8e8',
                  cursor: 'pointer',
                  backgroundColor: isActive ? '#f0f7ff' : 'white',
                  boxShadow: isActive
                    ? '0 2px 8px rgba(25,118,210,0.12)'
                    : '0 1px 3px rgba(0,0,0,0.04)',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    borderColor: '#c5c1c1ff',
                    backgroundColor: '#fafcff',
                  },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    color={isActive ? 'primary.main' : 'text.disabled'}
                    fontWeight={isActive ? 800 : 400}
                    display="block"
                    noWrap>
                    Chapter {chapter.order || idx + 1}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation()
                    removeChapter(idx)
                  }}
                  color="error"
                  disabled={!bookId || removingIndex === idx}
                  sx={{ opacity: 0.4, '&:hover': { opacity: 1 } }}>
                  {removingIndex === idx ? (
                    <CircularProgress size={14} color="error" />
                  ) : (
                    <DeleteIcon sx={{ fontSize: 14 }} />
                  )}
                </IconButton>
              </Box>
            )
          })}
        </Box>
      </Box>

      {/* Right Panel - All chapter details inline */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
        {selectedChapter ? (
          <>
            {/* Header */}

            {/* Scrollable content */}
            <Box
              sx={{ flex: 1, minWidth: 0, overflow: 'auto', p: 2 }}
              key={chapterKey}>
              {/* Basic fields */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  minWidth: 0,
                  overflow: 'hidden',
                }}>
                <NumberInput
                  source={`chapters.${selectedChapterIndex}.order`}
                  label="Order"
                  variant="standard"
                  sx={{ width: 100, flexShrink: 0 }}
                />
                <TextInput
                  source={`chapters.${selectedChapterIndex}.name`}
                  label="Chapter Name"
                  variant="standard"
                  sx={{ flex: 1, minWidth: 0 }}
                />
              </Box>

              {/* Voices + Attachments side by side */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                {/* Voices */}
                <Box
                  sx={{
                    flex: 1,
                    border: '1px solid #e8e8e8',
                    borderRadius: 2,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: 550,
                  }}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderBottom: '1px solid #e8e8e8',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: '#fafafa',
                    }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Voices
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      overflow: 'auto',
                      flex: 1,
                    }}>
                    {(selectedChapter.voices || []).length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No voices
                      </Typography>
                    )}
                    {(selectedChapter.voices || [])
                      .slice(0, multiVoices ? undefined : 1)
                      .map((_voice, vIdx) => (
                        <Box
                          key={`${selectedChapterIndex}-v-${vIdx}`}
                          sx={{
                            border: '1px solid #e8e8e8',
                            borderRadius: 1.5,
                            p: 1.5,
                            position: 'relative',
                          }}>
                          {multiVoices && (
                            <Box
                              sx={{ position: 'absolute', top: 8, right: 8 }}>
                              <IconButton
                                size="small"
                                onClick={() => removeVoice(vIdx)}
                                color="error"
                                sx={{
                                  opacity: 0.4,
                                  '&:hover': { opacity: 1 },
                                }}>
                                <DeleteIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Box>
                          )}
                          <SelectInput
                            source={`chapters.${selectedChapterIndex}.voices.${vIdx}.voiceId`}
                            label="Voice Actor"
                            choices={voiceActors.map(va => ({
                              id: va.id,
                              name: va.name,
                            }))}
                            variant="standard"
                            fullWidth
                          />
                          <CustomFileInput
                            source={`chapters.${selectedChapterIndex}.voices.${vIdx}.url`}
                            label="Audio File"
                            type="chapter"
                            upload={true}
                            showProgress>
                            <CustomFileField
                              source="src"
                              title="chapter"
                              variant="standard"
                            />
                          </CustomFileInput>
                          <FormDataConsumer>
                            {({ formData }) => {
                              const url =
                                formData?.chapters?.[selectedChapterIndex]
                                  ?.voices?.[vIdx]?.url
                              return typeof url === 'string' && url ? (
                                <Box sx={{ mt: 1 }}>
                                  <CustomAudioPlayerField
                                    audioPath={url}
                                    label="Preview"
                                    showInfo={false}
                                  />
                                </Box>
                              ) : null
                            }}
                          </FormDataConsumer>
                        </Box>
                      ))}
                  </Box>
                </Box>

                {/* Attachments */}
                <Box
                  sx={{
                    flex: 1,
                    border: '1px solid #e8e8e8',
                    borderRadius: 2,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: 550,
                  }}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderBottom: '1px solid #e8e8e8',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: '#fafafa',
                    }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Attachments
                    </Typography>
                    <Button
                      type="button"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={addAttachment}
                      variant="outlined">
                      Add
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      overflow: 'auto',
                      flex: 1,
                    }}>
                    {(selectedChapter.attachments || []).length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No attachments
                      </Typography>
                    )}
                    {(selectedChapter.attachments || []).map((_att, aIdx) => (
                      <Box
                        key={`${selectedChapterIndex}-a-${aIdx}`}
                        sx={{
                          border: '1px solid #e8e8e8',
                          borderRadius: 1.5,
                          p: 1.5,
                          position: 'relative',
                        }}>
                        <IconButton
                          size="small"
                          onClick={() => removeAttachment(aIdx)}
                          color="error"
                          sx={{ position: 'absolute', top: 4, right: 4 }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        <TextInput
                          source={`chapters.${selectedChapterIndex}.attachments.${aIdx}.name`}
                          label="Attachment Name"
                          variant="standard"
                          fullWidth
                          validate={validateRequired('Attachment name')}
                        />
                        <CustomFileInput
                          source={`chapters.${selectedChapterIndex}.attachments.${aIdx}.url`}
                          label="File"
                          type="attachment"
                          accept="text/*, image/*, application/msword, application/pdf"
                          upload={true}
                          validate={validateRequired('Attachment file')}>
                          <CustomFileField
                            source="src"
                            title="attachment"
                            canDownload
                          />
                        </CustomFileInput>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Footer - Save Chapter button */}
            <Box
              sx={{
                p: 1.5,
                borderTop: '1px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'flex-end',
                flexShrink: 0,
              }}>
              <Button
                type="button"
                variant="contained"
                startIcon={
                  saving ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
                onClick={saveChapter}
                disabled={!bookId || saving}
                size="small">
                {saving ? 'Saving...' : 'Save Chapter'}
              </Button>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}>
            <Typography variant="body2" color="text.secondary">
              Select a chapter to edit
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default ChaptersManagerInput
